import React from 'react';
import { go } from './nav.js';
import { useSaved, markSaved } from './saved.js';
import { bandFor, bandHref, brandHref } from './placement.js';
import { getToken, call } from './api.js';
import { needsAccount } from './gate.jsx';
import { zh, offerTitle, dish as dishName } from './i18n.js';
import { micAvailable, listen, speechTag, myLanguage, voiceFlags, knownVoiceFlags, recordAvailable, listenOnEleat, hearLive } from './voice.js';
import { openShare, SHARE_PATHS } from './share.js';
import { useDevice, metresBetween } from './place.js';
import { howFar } from './directions.js';

/* ══════════════════════════════════════════════════════════════════════════
 * THE PIECES MORE THAN ONE SCREEN DRAWS
 *
 * ⚠️ WHY THEY MOVED HERE, 22 September 2026. A venue card and the tab bar were
 * written inside Home.jsx when Home was the only screen that had them. Explore
 * needs both; the five View All pages need both; Saved needs the card.
 *
 * Copying either one would have been the fault this repository has spent a
 * week paying for — the /v1 prefix in two places, sixteen connector files, two
 * sign-in doors, two lists of folders, two ways of spelling a venue's slug.
 * Every one of them was one truth written twice and left to drift.
 *
 * A card that shows a rating on Home and not on Explore is the same failure
 * wearing a friendlier face: nobody reports it, because each screen looks
 * fine on its own.
 * ══════════════════════════════════════════════════════════════════════════ */

/**
 * One venue, as a card.
 *
 * @param tab  which tab to open the venue at. Home's rails pass their own —
 *             tapping a venue in the Happy Hour rail lands on Happy Hour, not
 *             About. Explore passes nothing, because a search result is about
 *             the venue rather than one of its offers.
 */
/* ⚠️ `railType` TELLS THE CARD WHICH ROW IT IS STANDING IN, and the badge
 * depends on it: the same venue is "ENDS IN 20 MIN" in the Last-Minute rail
 * and "TODAY 8 PM" in Game Day. Without it every card would pick the same
 * badge everywhere, which is how one venue ends up shouting about a deal the
 * row it is in has nothing to do with. */
/* ⚠️ `placement` IS THE SERVER'S ANSWER FOR THIS RAIL, and it only ever
 * arrives on Home. Explore, the five View All pages and Saved pass nothing,
 * which is why this parameter defaults to null rather than to an object: a
 * default shape would let a screen that never asked start drawing a band. */
/* ══════════════════════════════════════════════════════════════════════════
 * ⚠️⚠️⚠️ THE HEART IS TAPPABLE ON EVERY CARD — AL, 26 SEPTEMBER 2026.
 *
 * *"A person signs in and wants to heart a restaurant. If they're already
 * signed in, great, they can heart it. And at a later date, they can unheart
 * it. What's so fucking difficult about that?"*
 *
 * Tap it → saved. Tap it again → gone. Home, Explore, all five View Alls,
 * Saved, and Zaryna's answers. **One behaviour, no screen where it is
 * decoration.**
 *
 * ⚠️ WHAT I HAD BUILT INSTEAD, AND WHY IT WAS WRONG. `.card .heart` carried
 * `pointer-events: none` so the heart was deliberately dead on four screens,
 * and `verify_venue_card.js` REFUSED any attempt to give it an onClick. When he
 * asked for Saved's buttons I carved out one screen and reported back "the
 * heart is tappable on Saved only" — which he read, correctly, as gibberish.
 * **He was describing the obvious behaviour and I was defending an exception.**
 *
 * ⚠️ SIGNED OUT, IT ASKS — IT DOES NOT FAIL SILENTLY. Saving needs an account
 * (`POST /restaurants/:id/save` is requireAuth), so an untapped heart on a
 * signed-out phone goes to the sign-in screen rather than doing nothing. A
 * control that swallows a tap is the dead control this project keeps clearing.
 *
 * ⚠️ AND IT ANSWERS IMMEDIATELY, THEN WRITES. `markSaved()` flips the shared
 * set so every card on screen redraws at once — otherwise the same restaurant
 * in two rails would show two different hearts. **If the write fails it flips
 * back**, because a heart that filled and saved nothing is a silent lie.
 *
 * ⚠️ `onRemove` IS NOT A SECOND BEHAVIOUR — it is Saved asking to be told, so
 * the row can leave a list of saved places when it stops being one. The tap
 * does the same thing on every screen either way.
 * ══════════════════════════════════════════════════════════════════════════ */
/* which screen a card sits on, for the venue page's "came from" — read off
   the address the card is drawn under (3 Oct 2026, Restaurant Analytics) */
function screenHere() {
  const q = new URLSearchParams(window.location.search);
  if (q.has('explore')) return (q.get('q') || '').trim() ? 'search' : 'explore';
  if (q.has('all')) return 'view_all';
  if (q.has('saved')) return 'saved';
  if (q.has('zaryna')) return 'zaryna';
  /* ⚠️ HOME IS HOME WHATEVER ELSE IS IN THE ADDRESS — walked live, 12 Oct
     2026: Back lands on `?home=1` and shared links carry tags, so every Home
     card was recorded as from 'other' and lost its rail and slot. Only a
     real screen's key makes it another screen (main.jsx's list). */
  const SCREENS = ['venue', 'help', 'join', 'location', 'notifications', 'notifsettings', 'preferences',
    'profile', 'reservations', 'reserve', 'reset', 'reviews', 'settings', 'signin', 'welcome'];
  return SCREENS.some((k) => q.has(k)) ? 'other' : 'home';
}

/* ⚠️ ONE CLOCK FOR EVERY CARD — 8 October 2026 (Al: "will it change as the
   times change?"). The badges are worked out when the card draws, so a Home
   left open on the screen kept "STARTS 5 PM" past five and "ENDS IN 45 MIN"
   for ever. One shared once-a-minute tick (not one per card), running only
   while a card that shows a badge is on screen and the tab is visible. */
const minuteSubs = new Set();
let minuteTimer = null;
function minuteTick() {
  if (typeof document !== 'undefined' && document.hidden) return;
  minuteSubs.forEach((f) => { try { f(); } catch { /* one card cannot stop the rest */ } });
}
function useMinute(on) {
  const [, setN] = React.useState(0);
  React.useEffect(() => {
    if (!on) return undefined;
    const f = () => setN((n) => n + 1);
    minuteSubs.add(f);
    if (!minuteTimer) minuteTimer = setInterval(minuteTick, 60000);
    return () => {
      minuteSubs.delete(f);
      if (!minuteSubs.size && minuteTimer) { clearInterval(minuteTimer); minuteTimer = null; }
    };
  }, [on]);
}

export function VenueCard({ v, tab, railType, placement = null, onRemove = null, share = null, slot = null, reason = null }) {
  useMinute(!!railType);
  /* ⚠️⚠️ HOW FAR IS FROM THE PHONE, THE SAME AS THE RESTAURANT PAGE —
     30 September 2026. The server's `distance_km` is measured from the SEARCH
     point, which is the chosen place when somebody has chosen one. So an
     Eleater standing in Hong Kong who chose Calgary saw "0.9 km" on every
     Calgary card while the restaurant page, which measures from the phone
     (Al, 27 Sep: *"it's where I'm fucking standing"*), said thousands.
     Same rule as Venue.jsx, same functions: the phone when it has reported,
     the server's figure (the chosen place) only when it has not, and nothing
     when the phone's own error is too coarse to name a distance — `howFar`'s
     rule, reused rather than restated. `location_lat != null` first, because
     `Number(null)` is 0 and 0 is a place. */
  const device = useDevice();
  const phone = device && Number.isFinite(device.lat);
  const hasCoords = v.location_lat != null && v.location_lng != null;
  let km = null;
  if (phone && hasCoords) {
    const d = metresBetween(device, { lat: Number(v.location_lat), lng: Number(v.location_lng) }) / 1000;
    km = (Number.isFinite(d) && howFar(d, device.accuracy_m) != null) ? d : null;
  } else if (!phone && v.distance_km != null && Number.isFinite(Number(v.distance_km))) {
    km = Number(v.distance_km);
  }
  const from = screenHere();
  const href = `?venue=${encodeURIComponent(v.id)}`
    + (tab ? `&tab=${tab}` : '')
    + `&from=${from}`
    /* the words searched travel with the tap (Analytics' "Search words that
       found you", 24 Aug) */
    + (from === 'search' ? `&sq=${encodeURIComponent(String(new URLSearchParams(window.location.search).get('q') || '').trim().slice(0, 120))}` : '')
    + (from === 'home' && railType && slot ? `&rail=${encodeURIComponent(railType)}&slot=${slot}` : '');

  /* ══════════════════════════════════════════════════════════════════════
     ⚠️⚠️ THE SPONSORED BAND — DESIGNED, BUILT IN THE DATABASE, DRAWN BY THE
     LOCKED SCREEN, AND ABSENT FROM REACT ENTIRELY.

     `10_Home_LOCKED.html` has carried `.spon-band` since 15 September 2026 and
     `Eleater Prototype/eleat_campaign.js` has known how to paint it since the
     same day. The React card drew a photograph, a badge, a heart, a name and a
     meta line — so a brand paying for a month in a city reached nothing at
     all, on the one surface it is paying for.

     ⚠️ IT SAYS "SPONSORED BY", THE SAME WORD AS THE BADGE, EVERY TIME.
     "Featured" read as an endorsement and was banned; "Presented by" was a
     third label for one fact and is dead too. Migration 075 §3: the badge says
     it is paid, the card says who paid.

     ⚠️ AND IT IS A CONTROL, NOT A LABEL — the one thing on this card that is.
     The heart here is an indicator by Al's own decision; the band is the
     opposite, and that is also his: *"if a restaurant has a purple sponsorship
     and they press it, they should go directly to Game Day, not the about page
     of the restaurant."*  `10_Home_LOCKED.html` records it as
     `.spon-band{cursor:pointer}`.

     ⚠️ A `<span>` WITH A ROLE, NOT AN `<a>`. The whole card is already an
     anchor and an anchor inside an anchor is invalid markup that browsers
     repair by splitting the card in two — which would break the card, not just
     the band. So it is a span that takes the keyboard and stops the click from
     reaching the card underneath, which would otherwise open About: the exact
     destination Al said it must not. */
  const sponsor = bandFor(v, placement, railType);
  const openSponsored = (e) => {
    e.preventDefault();
    e.stopPropagation();
    go(bandHref(v.id));
  };

  /* ⚠️ THE HEART WAS MISSING FROM THIS CARD ENTIRELY — until 23 September
     2026, and Al found it on his phone: *"even the heart is fucked."*
     `10_Home_LOCKED.html` draws `<svg class="heart">` on every card in all
     four rails; this component drew the photograph, the name and the meta
     line and stopped. Nothing errored, nothing was logged, and the rails
     looked plausible — which is exactly why it survived five screens.

     ⚠️ INDICATOR, NOT A CONTROL — AL'S OWN DECISION, and the reason it has no
     onClick. `eleat_hearts.js` quotes him, 28 August 2026: *"those should be
     only clickable when they are in the restaurant page."* Saving happens on
     the venue's own page, where `Venue.jsx` already has the button. Out here
     it answers one question — have I already saved this place — so the same
     red heart follows a restaurant through Home, Explore, every View All and
     Zaryna's answers.

     ⚠️ AND IT IS THE LOCKED PATH, COPIED, NOT DRAWN AGAIN. A second
     hand-written heart is a second heart that drifts. */
  const savedIds = useSaved();
  const heartOn = savedIds.has(v.id);

  /* the tap — one function, used by every card on every screen */
  function toggleHeart(e) {
    e.preventDefault();
    e.stopPropagation();          /* the card is an anchor; do not open it */
    if (needsAccount('save', heartOn ? null : { save: v.id })) return;
    const on = !heartOn;
    markSaved(v.id, on);          /* every card redraws now, not after the write */
    call(`/restaurants/${encodeURIComponent(v.id)}/save`,
      { method: on ? 'POST' : 'DELETE', auth: true })
      .then((d) => {
        /* trust the server's answer over assuming the flip worked — POST
           answers {saved:true}, DELETE answers {saved:false} */
        const now = d && typeof d.saved === 'boolean' ? d.saved : on;
        markSaved(v.id, now);
        if (!now && onRemove) onRemove(v);
      })
      .catch(() => markSaved(v.id, heartOn));   /* put it back */
  }

  /* ⚠️ TWO FIELDS, AND I READ THE WRONG ONE — 22 September 2026.
     `rating_shown` is a BOOLEAN: `coalesce(agg.meets_threshold, false)`. The
     number lives in `rating`, which the server sets to null until five
     eligible reviews exist. This card rendered `★ {rating_shown}` — every
     venue with a rating would have said **★ true**, and every one without
     would have said nothing, so the bug would have looked like a styling
     problem on half the cards.

     ⚠️ CAUGHT BY READING THE SELECT, NOT THE SCREEN, and only because Saved
     forced me to compare two shapes. Nothing in the app would have thrown.

     The rule itself is the server's and stays there: a rating needs five
     eligible reviews, `rating_threshold` comes back in the same answer, and
     no screen needs to know the number 5. */
  const rating = v.rating_shown ? v.rating : null;
  const meta = [
    /* ⚠️ ONE DECIMAL. The API returns numeric(3,2) as the string "4.80", so
       every card read "★ 4.80". The locked cards show ★ 4.8. Number() first,
       because toFixed on a string throws. */
    rating != null ? `★ ${Number(rating).toFixed(1)}` : null,
    priceBand(v),
    zh(v.location_name, v.location_name_zh) || null,
  ].filter(Boolean).join(' · ');

  const badge = badgeFor(v, railType);
  const chips = chipsFor(v, railType);

  /* ⚠️ HELD IN A CONST, NOT RETURNED — a Saved card wraps it (see below), and
     every other screen returns it untouched. */
  const card = (
    <a className={sponsor ? 'card sponsored' : 'card'} href={href}>
      <div className="img">
        {v.cover_url
          ? <img src={v.cover_url} alt="" loading="lazy" />
          /* ⚠️ A VENUE WITH NO PHOTOGRAPH GETS A QUIET PANEL — not a broken
             image icon, and never a stand-in picture of another restaurant. */
          : <div className="noimg" aria-hidden="true" />}
        {/* ⚠️ ON THE PHOTOGRAPH, TOP-LEFT — `10_Home_LOCKED.html .badge`. */}
        {badge && <span className={`badge ${badge.cls}`}>{badge.text}</span>}
        {/* ⚠️ `stroke="#fff"` IS THE LOCKED SCREEN'S OWN VALUE, quoted from
            `10_Home_LOCKED.html .card .heart`, and it stays a literal for the
            reason RULE ZERO-A gives: this outline sits on top of a
            PHOTOGRAPH, not on an Eleat surface, so it is white against
            whatever the picture happens to be rather than a brand colour.
            The filled state is the token — `.heart.on` in eleat.css reads
            `var(--heart)`, which is the palette's #AD1A1A.

            ⚠️ aria-hidden, because it is not a control and a screen reader
            announcing a heart would offer something that cannot be tapped. */}
        {/* ⚠️ A BUTTON ON EVERY CARD, NOT A PICTURE. `aria-pressed` and the
            title both say which way it is, so a screen reader gets the same
            answer the colour gives — and the title is what a long-press shows.
            Same path, same classes, so nothing about it LOOKS different. */}
        <button type="button"
          className={heartOn ? 'heart on' : 'heart'}
          aria-pressed={heartOn}
          title={heartOn ? 'Remove from saved' : 'Save'}
          aria-label={heartOn ? 'Remove from saved' : 'Save'}
          onClick={toggleHeart}>
          <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"
            strokeLinejoin="round" aria-hidden="true">
            <path d="M12 20.5l-1.3-1.2C5.5 14.7 2.2 11.7 2.2 8 2.2 5.4 4.3 3.4 6.9 3.4c1.5 0 2.9.7 3.8 1.8L12 6.6l1.3-1.4c.9-1.1 2.3-1.8 3.8-1.8 2.6 0 4.7 2 4.7 4.6 0 3.7-3.3 6.7-8.5 11.3L12 20.5z" />
          </svg>
        </button>
        {/* ⚠️ ACROSS THE BOTTOM OF THE PHOTOGRAPH — `10_Home_LOCKED.html
            .spon-band` is `position:absolute;left:0;right:0;bottom:0`, so it
            belongs inside `.img` and nowhere else. */}
        {sponsor && (
          <span className="spon-band" role="link" tabIndex={0}
            onClick={openSponsored}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') openSponsored(e);
            }}>
            <small>SPONSORED BY</small>
            {sponsor.brand}
          </span>
        )}
      </div>
      <div className="body">
        {/* in the reader's language: the restaurant's Chinese name (136) for a
            Chinese reader, as OpenRice shows it; its own name otherwise */}
        <div className="name">{zh(v.name, v.name_zh)}</div>
        {meta && <div className="meta">{meta}</div>}
        {/* ⚠️ WHAT THE OFFER ACTUALLY IS, in the restaurant's own words. */}
        {chips.length > 0 && (
          <div className="tags">
            {chips.map((t) => <span key={t} className="tag deal">{t}</span>)}
          </div>
        )}
        {/* ⚠️ THE DISH THEY ASKED FOR, ON ZARYNA'S CARD — Al-Noor, 16 Oct 2026:
            "these are the five restaurants that have chicken wings… let the
            rest of the people go through the menu." The restaurant's own dish
            and price (the server's match, in the reader's language). */}
        {v.dish && v.dish.name && (
          <div className="dishline">
            {dishName(v.dish).name}{priceText(v.dish.price) ? ` · ${priceText(v.dish.price)}` : ''}
          </div>
        )}
        {/* the game it is showing, on Zaryna's card for a game question */}
        {railType !== 'game_day' && v.game_line && <div className="gameline">{v.game_line}</div>}
        {/* her one reason for this place, under its own card */}
        {reason && <div className="zwhy">{reason}</div>}
        {/* ⚠️ THE GAME, ON THE GAME DAY RAIL — Al, 1 October 2026: *"we have
            to have the match name and league etc."*  The match (or the event —
            a race, a fight card, a round) and its competition, from the
            venue's next game today. The time is on the badge above. */}
        {railType === 'game_day' && v.game_day && v.game_day.title && (
          <div className="gameline">
            {v.game_day.title}{v.game_day.league ? ` · ${v.game_day.league}` : ''}
          </div>
        )}
        {/* ⚠️ OPEN OR NOT, AND HOW FAR — the locked card's `.row-hours` and
            `.row-addr`. `distance_km` only arrives when Eleat knows where the
            Eleater is, so the distance half simply does not draw otherwise
            rather than printing a dash. */}
        {(v.open_now != null || km != null) && (
          <>
            {v.open_now != null && (
              <div className="row-hours">
                <span className={v.open_now ? 'open' : undefined}>
                  {v.open_now ? 'Open now' : 'Closed now'}
                </span>
                <span className="chev" aria-hidden="true">›</span>
              </div>
            )}
            {v.location_name && (
              <div className="row-addr">
                <span className="addr">{zh(v.location_name, v.location_name_zh)}</span>
                {km != null && (
                  <span>{km.toFixed(1)} km</span>
                )}
              </div>
            )}
          </>
        )}
        {/* ⚠️ `08a_Saved.html .viewcue` — "View restaurant ›". It exists because
            the card body is tappable and nothing else on a saved card says so.
            Only where the locked screen draws it. */}
        {share && <div className="viewcue">View restaurant ›</div>}
      </div>
    </a>
  );

  /* ⚠️ THE SHARE BUTTON IS A SIBLING OF THE CARD, NOT A CHILD — and that is
     structural, not cosmetic. `08a_Saved.html` closes `.cardtop` and THEN
     writes `.sharebtn`, because the card is an anchor and a button inside an
     anchor is a control the browser will still let the anchor swallow. Outside
     it, a tap on Share cannot open the restaurant by accident. */
  if (!share) return card;
  return (
    <div className="savedcard">
      {card}
      <button type="button" className="sharebtn"
        onClick={() => openShare(share)}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="1.9" strokeLinejoin="round"
          aria-hidden="true">
          {SHARE_PATHS.map((d) => <path key={d} d={d} />)}
        </svg>
        <span>Share</span>
      </button>
    </div>
  );
}


/* ══════════════════════════════════════════════════════════════════════════
 * THE BRAND'S OWN CARD, IN THE ROW
 *
 * Al, 15 September 2026: *"what happened to every fifth card is gonna have an
 * advertisement on Game Day and Happy Hour and stuff like that? Fuck. What
 * about all that shit? Where did that go?"*
 *
 * ⚠️ IT WENT INTO THE DATABASE AND STOPPED THERE, TWICE. Migration 075 wrote
 * the three settings and nothing read them; `eleat_campaign.js` then read them
 * on the locked screen and React had never heard of any of it.
 *
 * ⚠️ THE SAME BADGE AS A SPONSORED VENUE CARD, AND THE SAME WORD. A bigger,
 * richer, more expensive advertisement is MORE reason for the disclosure to be
 * unmistakable, not less — migration 075 §3.
 *
 * ⚠️ THE STILL FRAME, AND THE FILM RUNS WHEN IT IS TAPPED.
 * `brand_card.video_autoplay` is false and `brand_card.video_muted` is true:
 * sound firing out of a phone in a restaurant is hostile, and video that plays
 * unasked burns an Eleater's data in a market where people notice. Both are
 * settings and both are read, never assumed — the `<video>` below takes them
 * from the server's answer, so flipping either one in the Super Admin changes
 * what happens without a deploy.
 * ══════════════════════════════════════════════════════════════════════════ */
export function BrandCard({ card, placement, railType }) {
  const [playing, setPlaying] = React.useState(false);
  if (!card || !card.brand) return null;

  const film = card.video && card.poster ? card.video : null;
  const autoplay = placement ? placement.video_autoplay === true : false;
  const muted = placement ? placement.video_muted !== false : true;

  return (
    <a className="card sponsored brandcard" href={brandHref(railType)}
      data-brand={card.brand}>
      <div className="img">
        {/* ⚠️ THE POSTER IS REQUIRED WHENEVER THERE IS A FILM — the table
            itself refuses the other combination (`a_video_needs_a_still`), and
            a card with no still frame would have to start playing by itself to
            show anything at all, which is the one thing it must not do. */}
        {playing && film
          ? <video src={film} poster={card.poster} controls autoPlay
              muted={muted} playsInline />
          : card.poster
            ? <img src={card.poster} alt="" loading="lazy" />
            : <div className="noimg" aria-hidden="true" />}
        {film && !playing && (
          <button type="button" className="brandplay" aria-label="Play"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setPlaying(true); }}>
            ▶
          </button>
        )}
        {/* ⚠️ AUTOPLAY IS A SETTING AND IT IS OFF. When the Super Admin turns
            it on, the film starts on its own and the play mark never draws —
            which is why this is one line reading the switch rather than two
            components. */}
        {film && autoplay && !playing && <AutoPlay onStart={() => setPlaying(true)} />}
        <span className="spon-pill">SPONSORED</span>
      </div>
      <div className="body">
        <div className="name">{card.brand}</div>
        {/* ⚠️ THE BRAND'S OWN WORDS, NEVER ELEAT'S. `brand_creatives.headline`
            is what the brand typed and what a person reviewed and approved.
            Eleat does not write copy for an advertisement it is paid to
            carry, and it does not rephrase it either. */}
        {card.headline && <div className="meta">{card.headline}</div>}
        {card.body && <div className="brandbody">{card.body}</div>}
        {card.cta_label && (
          <div className="row-hours">
            <span className="open">{card.cta_label}</span>
            <span className="chev" aria-hidden="true">›</span>
          </div>
        )}
      </div>
    </a>
  );
}

/* ⚠️ A SEPARATE COMPONENT BECAUSE A HOOK CANNOT LIVE INSIDE A CONDITION.
   Starting the film from inside `BrandCard`'s body would be a state update
   during render; an effect is the right place and an effect must not be
   written behind an `if`. */
function AutoPlay({ onStart }) {
  React.useEffect(() => { onStart(); }, [onStart]);
  return null;
}


/* ══════════════════════════════════════════════════════════════════════════
 * ⚠️⚠️ THE BADGES — EVERY ONE OF THEM WAS MISSING FROM THE REACT CARD.
 *
 * `10_Home_LOCKED.html` puts a badge on the photograph and a row of chips
 * under the name. The React card drew a picture, a heart, a name and a meta
 * line, and nothing else — so the Last-Minute rail had no urgency, Game Day
 * had no kick-off, and no card ever said what the offer actually was.
 *
 * ⚠️ AND THE SEARCH DID NOT SEND THE DATA EITHER, which is why this looked
 * like a card problem for so long. `/discovery/search` now returns `offers`,
 * `open_now` and `price_band` — the card change alone would have rendered
 * nothing.
 *
 * ⚠️ RULE ZERO-B — THE BADGE VALUES IN THE LOCKED FILE ARE SAMPLE CONTENT.
 * "ENDS IN 15 MIN" and "TODAY 8 PM" are not constants to copy; they are the
 * SHAPES. The minutes come from the deal's own `ends_at` and the hour from
 * the Game Day deal. A hard-coded 15 would be a lie on every card.
 * ══════════════════════════════════════════════════════════════════════════ */

/* ⚠️ THE FOUR KEYS ARE THE SCHEMA'S — happy_hour · daily · last_minute ·
 * game_day (migration 001, never altered). */
function offersOf(v) {
  return Array.isArray(v.offers) ? v.offers : [];
}

/** Minutes from now until `iso`, or null when there is no honest answer. */
function minutesUntil(iso) {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  const mins = Math.round((t - Date.now()) / 60000);
  return mins > 0 ? mins : null;
}

/* A menu price the way the locked screen prints it — `11_Restaurant.html`
   shows "68", never "68.00". The database keeps numeric(10,2), so a whole
   number arrives as "68.00"; cents are kept only when there are cents. One
   formatter for every menu, so no two menus print the same price two ways. */
export function priceText(p) {
  if (p === null || p === undefined || p === '') return null;
  const n = Number(p);
  if (!Number.isFinite(n)) return String(p);
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}

/* TODAY, the weekday within six days (FRI), or the date beyond (SAT 10 OCT)
   — in the VENUE's own calendar, the same rule as its hour below */
function venueDayText(v, iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const tz = v.timezone || undefined;
  const ymd = (x) => {
    try { return new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(x); }
    catch { return x.toISOString().slice(0, 10); }
  };
  const days = Math.round((Date.parse(ymd(d)) - Date.parse(ymd(new Date()))) / 86400000);
  if (days <= 0) return 'TODAY';
  let wd; let dm;
  try {
    wd = new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'short' }).format(d).toUpperCase();
    dm = new Intl.DateTimeFormat('en-GB', { timeZone: tz, day: 'numeric', month: 'short' }).format(d).toUpperCase();
  } catch { return null; }
  return days <= 6 ? wd : `${wd} ${dm}`;
}

/* "8 PM" in the VENUE's hour, not the browser's — 30 September 2026.
   `getHours()` reads the viewer's own clock, so a Hong Kong game seen from
   Calgary printed Calgary's hour. Every search row carries the venue's
   `timezone`; with none, the browser's is all there is. */
function venueHourText(v, iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  let h = d.getHours();
  let m = d.getMinutes();
  if (v.timezone) {
    try {
      const parts = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', hourCycle: 'h23', timeZone: v.timezone })
        .formatToParts(d);
      const hp = parts.find((p) => p.type === 'hour'); const mp = parts.find((p) => p.type === 'minute');
      if (hp && Number.isFinite(Number(hp.value))) h = Number(hp.value) % 24;
      if (mp && Number.isFinite(Number(mp.value))) m = Number(mp.value);
    } catch { /* an unknown zone name: keep the browser's hour */ }
  }
  /* the minutes when the game does not start on the hour — a 6:45 PM
     kick-off read "6 PM" (audit, 7 Oct 2026); on the hour it stays the
     locked "8 PM" */
  const mm = m ? `:${String(m).padStart(2, '0')}` : '';
  /* 151 — the venue's own clock: "17:00" when it chose 24-hour */
  if (v.time_format === '24h') return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  const hour12 = ((h + 11) % 12) + 1;
  return `${hour12}${mm} ${h < 12 ? 'AM' : 'PM'}`;
}

/* "17:00" (the venue's own clock time, as the server sends it) in the venue's
   12h/24h choice — "5 PM", "7:30 PM" or "17:00" (151) */
function clockText(v, hhmm) {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(hhmm || ''));
  if (!m) return null;
  const h = Number(m[1]) % 24; const mi = Number(m[2]);
  if (v.time_format === '24h') return `${String(h).padStart(2, '0')}:${m[2]}`;
  return `${((h + 11) % 12) + 1}${mi ? ':' + m[2] : ''} ${h < 12 ? 'AM' : 'PM'}`;
}

/* ⚠️ HAPPY HOUR AND DAILY DEALS: ON NOW, OR WHEN IT STARTS — 8 October 2026.
   Al: "do it… the same way… as game day". The row listed every venue with a
   happy hour, on or not, so a 5–7 pm happy hour sat there at 10 am looking
   exactly like one that was on, under "Happy Hours Happening Now". Researched
   (LastCall, Clinkly, SnappyHour): the happy-hour apps show a STATUS — on now
   or starting soon — not a ticking timer, so the ticking "ENDS IN" stays
   Last-Minute's. `today` is the server's answer off the restaurant's own
   hours, on its own clock (live.sql.js todayWindow): on now → until when;
   later today → when it starts; finished or not today → nothing. */
/* the venue's own "HH:MM" right now */
function venueNowHHMM(v) {
  try {
    return new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
      timeZone: v.timezone || undefined }).format(new Date());
  } catch { return null; }
}
/* the server answered when the card was fetched; the minute tick re-asks the
   clock, so "STARTS 5 PM" turns into "ON NOW" at five and a window that has
   closed drops its badge — without fetching anything */
function stillTrue(v, w) {
  if (!w || w.all_day || !w.from || !w.to) return w;
  const now = venueNowHHMM(v);
  if (!now) return w;
  const crosses = w.to < w.from;
  const inside = crosses ? (now >= w.from || now < w.to) : (now >= w.from && now < w.to);
  if (inside) return { ...w, on_now: true };
  if (w.on_now || (!crosses && now >= w.to)) return null;   // finished for today
  return { ...w, on_now: false };
}
function todayOf(v, type) {
  const ws = offersOf(v).filter((o) => o.type === type && o.today).map((o) => stillTrue(v, o.today)).filter(Boolean);
  return ws.find((w) => w.on_now && !w.all_day) || ws.find((w) => w.on_now)
    || ws.filter((w) => w.from).sort((a, b) => String(a.from).localeCompare(String(b.from)))[0] || null;
}

/* the cards that are on now first, each group in the order it came in (the
   rotation is untouched within each) — Happy Hour and Daily Deal rows only */
export function onNowFirst(rows, railType) {
  if (railType !== 'happy_hour' && railType !== 'daily') return rows;
  const on = (v) => { const w = todayOf(v, railType); return !!(w && w.on_now && v.open_now !== false); };
  return [...rows.filter(on), ...rows.filter((v) => !on(v))];
}

/* ⚠️ THE BADGE ON THE PHOTOGRAPH — ONE, NEVER A STACK. The locked card shows
 * a single badge top-left; two would cover the picture and compete. Order of
 * precedence is the locked file's own: a Last-Minute that is running out is
 * more urgent than a game that is on, which is more urgent than one later. */
function badgeFor(v, railType) {
  const offers = offersOf(v);
  const find = (t) => offers.find((o) => o.type === t);

  /* only on the Last-Minute rail — `|| !railType` also put ENDS IN on
     Explore, Saved and Zaryna's cards, and the locked explore.html and
     08a_Saved.html carry no badge at all (audit, 7 Oct 2026) */
  if (railType === 'last_minute') {
    const lm = find('last_minute');
    const mins = lm && minutesUntil(lm.ends_at);
    /* ⚠️⚠️ MINUTES UNDER THE HOUR, HOURS ABOVE IT — AND AL HAD TO SAY SO.
       My first version printed minutes up to two hours, so a deal with 95
       left would have read "ENDS IN 95 MIN". Al: *"what the fuck is 380
       minutes? Why can't you say X amount of hours."*  He is right, and it
       was not a near miss — nobody has ever said "ninety-five minutes" about
       a happy hour. A person reads minutes when it is nearly over and hours
       when it is not.

       ⚠️ AND THE BADGE STOPS AT FOUR HOURS. Past that it is not urgency, it
       is opening times, and the card already says whether the place is open.
       A deal that has already ended shows nothing at all — `minutesUntil`
       answers null for anything in the past, so an expired badge cannot be
       drawn. */
    if (mins != null && mins <= 240) {
      if (mins < 60) return { cls: 'lm', text: `ENDS IN ${mins} MIN` };
      const hrs = Math.round(mins / 60);
      return { cls: 'lm', text: `ENDS IN ${hrs} ${hrs === 1 ? 'HOUR' : 'HOURS'}` };
    }
  }

  if (railType === 'happy_hour' || railType === 'daily') {
    const w = todayOf(v, railType);
    if (!w) return null;
    /* an all-day Daily Deal carries no badge: "ON TODAY" on every card in a
       row that is already today's said nothing (seen on screen, 8 Oct) */
    if (w.all_day) return null;
    const until = clockText(v, w.to); const from = clockText(v, w.from);
    /* ⚠️ NEVER "ON NOW" ON A CLOSED RESTAURANT — walk, 9 Oct 2026: the same
       card read "ON NOW · UNTIL 7 PM" and "Closed now". An offer is only on
       while the doors are open (the Game Day LIVE badge already asks this). */
    if (w.on_now && v.open_now === false) return null;
    if (w.on_now && until) return { cls: 'live', text: `ON NOW · UNTIL ${until}` };
    if (!w.on_now && from) return { cls: 'today', text: `STARTS ${from}` };
    return null;
  }

  /* ⚠️ THE GAMES FIRST — 1 October 2026. `v.game_day` is the venue's next
     game off 019's tables — on now, else the soonest in its schedule (live.sql.js gameDayNextLateral): LIVE while
     its window is on and the venue is open, otherwise TODAY and the
     KICK-OFF hour — the locked "TODAY 8 PM" is a kick-off, never the moment
     an offer ends. A Game Day deal row below is the fallback. */
  /* ⚠️ ONLY ON THE GAME DAY RAIL — 1 October 2026. Every search row now
     carries its next game, so without this every card on Happy Hour, Daily
     Deals, Last-Minute and Explore read LIVE. `10_Home_LOCKED.html` draws
     LIVE / TODAY in the Game Day rail and nowhere else. */
  if (railType !== 'game_day') return null;

  if (v.game_day && v.game_day.kickoff) {
    if (v.game_day.live && v.open_now) return { cls: 'live', text: 'LIVE' };
    const t = venueHourText(v, v.game_day.kickoff);
    /* ⚠️ GAME DAY IS NOT ONLY TODAY (Al, 1 Oct 2026). The locked badge is
       "TODAY 8 PM"; a game later this week keeps that shape with its day —
       "FRI 8 AM" — and one further out names the date — "SAT 10 OCT". */
    const day = venueDayText(v, v.game_day.kickoff);
    if (t && day === 'TODAY') return { cls: 'today', text: `TODAY ${t}` };
    if (t && day) return { cls: 'today', text: /\d/.test(day) ? day : `${day} ${t}` };
  }

  const gd = find('game_day');
  if (gd) {
    const mins = minutesUntil(gd.ends_at);
    /* ⚠️ "LIVE" MEANS NOW, AND THE VENUE MUST ACTUALLY BE OPEN. Saying a game
       is live at a place that shut an hour ago is the kind of confident wrong
       sentence that costs trust. */
    if (v.open_now && gd.ends_at && mins != null) return { cls: 'live', text: 'LIVE' };
    if (gd.ends_at) {
      const t = venueHourText(v, gd.ends_at);
      if (t) return { cls: 'today', text: `TODAY ${t}` };
    }
  }
  return null;
}

/* ⚠️ THE OFFER CHIPS ARE THE RESTAURANT'S OWN WORDS. `d.title` is what the
 * venue typed into its own admin — "Pitcher 88", "Tea set 39". Eleat never
 * writes these and never rephrases them. Three at most: the locked card shows
 * a short row, and a fourth wraps onto a second line and pushes the photo up. */
/* ⚠️ AND A CHIP THAT REPEATS THE ROW IT IS IN SAYS NOTHING. Checked against
 * the live data: some venues title their offer "Happy Hour" or "Daily Deal" —
 * the name of the row it is already sitting in. "Free Flow Wings, Fries &
 * Drinks" earns its space; "Happy Hour" under the Happy Hour heading is noise
 * that pushes the useful chips off the card. */
const GENERIC_OFFER_TITLES = new Set([
  'happy hour', 'daily deal', 'daily deals',
  'last-minute special', 'last minute special', 'game day',
]);

function chipsFor(v, railType) {
  return offersOf(v)
    /* ⚠️ A ROW SHOWS ITS OWN OFFER — 9 Oct 2026, seen through Al-Noor's eyes:
       An Choi in the Last-Minute row read "HAPPY HOUR EVERYDAY 12-7.30PM"
       (its Last-Minute offer is titled "Last-Minute Special" and is left out
       as the heading's own words, so its Happy Hour was the only chip). The
       locked 10_Home_LOCKED.html puts the row's offer on the card ("30% off
       mains" in Last-Minute). Explore and Saved have no row and keep them all. */
    .filter((o) => !railType || o.type === railType)
    .filter((o) => typeof o.title === 'string' && o.title.trim())
    .filter((o) => !GENERIC_OFFER_TITLES.has(o.title.trim().toLowerCase()))
    /* the restaurant's words in the reader's language — Zaryna's (139) */
    .map((o) => offerTitle(o))
    .slice(0, 3);
}

/* ⚠️ FIVE DESTINATIONS, FROM THE LOCKED SCREEN'S OWN NAV — `10_Home_LOCKED
   .html .ptabbar`: Home · Explore · Zaryna · Saved · Profile, in that order.

   ⚠️ ALL FIVE ARE LIVE AS OF 22 SEPTEMBER 2026. Each href was turned on in
   the same change that built its screen — a tab left dimmed after its screen
   exists is the same fault as a live-looking tab that opens nothing, seen
   from the other side, and both are the dead control this project keeps
   clearing out. The `href: null` branch below stays because the next market,
   the next screen or a feature switched off for a city will need it.

   ⚠️ AND PROFILE LEADS TO PROFILE, not straight to sign-in. A signed-in
   Eleater tapping Profile and landing on a sign-in form is being asked to do
   something they have already done. */
/* ══════════════════════════════════════════════════════════════════════════
 * ⚠️ THE ICONS WERE MISSING FROM ALL FIVE TABS, ON EVERY SCREEN
 *
 * Al, 23 September 2026: *"You don't even have Zaryna's face there or a circle
 * or anything like that. What constitutes this as a finished fucking app?"*
 *
 * This component rendered five words. Every locked screen carries five ICONS
 * with the word underneath — and Zaryna's sits in a raised persimmon circle
 * proud of the bar. ⚠️ THE LOCKED FILES PUT HER PHOTOGRAPH IN THAT CIRCLE AND
 * AL REMOVED IT ON 24 SEPTEMBER 2026: *"we dont need a zaryna profile pic."*
 * The circle is the locked screen's; what is inside it is `ZarynaMark`, which
 * is Eleat's own mark and pulses while she is listening.
 * It is on every screen in Eleat,
 * so one missing component was the same fault repeated eleven times, and each
 * screen looked plausible on its own.
 *
 * ⚠️ EVERY PATH BELOW IS COPIED FROM `10_Home_LOCKED.html`, not redrawn. A
 * second hand-drawn house is a second house that drifts — and the Saved heart
 * is the SAME path as the card heart in `VenueCard` above, which is not a
 * coincidence and must stay true.
 * ══════════════════════════════════════════════════════════════════════════ */
const HEART_PATH = 'M12 20.5l-1.3-1.2C5.5 14.7 2.2 11.7 2.2 8 2.2 5.4 4.3 3.4 6.9 3.4c1.5 0 2.9.7 3.8 1.8L12 6.6l1.3-1.4c.9-1.1 2.3-1.8 3.8-1.8 2.6 0 4.7 2 4.7 4.6 0 3.7-3.3 6.7-8.5 11.3L12 20.5z';

/* ⚠️ ONE HEART EVERYWHERE — Al-Noor, 11 Oct 2026: "the hearts are skew". The
   restaurant page drew a typed ♡/♥ (a font's character, its own shape and
   off-centre in the 38px disc) while every card draws HEART_PATH. Now the
   page draws this, sized by its own font-size (1em), outline or filled. */
export function HeartIcon({ on = false }) {
  return (
    <svg viewBox="0 0 24 24" width="1em" height="1em" fill={on ? 'currentColor' : 'none'}
      stroke="currentColor" strokeWidth="2" strokeLinejoin="round" aria-hidden="true"
      style={{ display: 'block' }}>
      <path d={HEART_PATH} />
    </svg>
  );
}

/* the four line icons, at the locked screen's 23×23 / stroke-width 2 */
const stroke = {
  width: 23, height: 23, viewBox: '0 0 24 24', fill: 'none',
  stroke: 'currentColor', strokeWidth: 2,
};

const ICONS = {
  home: () => (
    <svg {...stroke}><path d="M3 11l9-8 9 8" /><path d="M5 10v10h14V10" /></svg>
  ),
  explore: () => (
    <svg {...stroke}><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></svg>
  ),
  saved: () => (<svg {...stroke}><path d={HEART_PATH} /></svg>),
  /* ⚠️ HER MARK, NOT HER PHOTOGRAPH — AND THAT IS A DEPARTURE FROM THE LOCKED
     SCREEN, ON AL'S OWN INSTRUCTION, 24 September 2026: *"we dont need a
     zaryna profile pic and because google and siri dont have one."*
     `10_Home_LOCKED.html` draws `zaryna.jpg` in this slot. It does not any
     more, anywhere in Eleat. The raised persimmon circle stays — that is the
     locked bar's shape, and only what sits inside it has changed. */
  zaryna: () => (
    <span className="pzwrap"><ZarynaFace /></span>
  ),
  /* ⚠️ FILLED, NOT STROKED — the locked file draws this one with `fill` and
     switches the fill between rest and active in CSS, so no stroke here. */
  profile: () => (
    <span className="pav">
      <svg width="14" height="14" viewBox="0 0 24 24">
        <circle cx="12" cy="9" r="3.4" />
        <path d="M5.5 20c0-3.6 3-5.6 6.5-5.6s6.5 2 6.5 5.6z" />
      </svg>
    </span>
  ),
};

const NAV = [
  { key: 'home',    label: 'Home',    href: '?' },
  { key: 'explore', label: 'Explore', href: '?explore=1' },
  /* ⚠️⚠️ "Ask Zaryna", AL'S OWN CHOICE, 26 SEPTEMBER 2026 — *"Let's use Ask
     Zarina."*  He picked it from three after the research, and the research is
     why the label exists at all: a bottom bar holds DESTINATIONS, not actions,
     so a microphone cannot live here — and Material's own guidance says
     *"Icons alone are risky if the meaning is not obvious. Pair icons with
     short labels for clarity."*  He put it plainly: *"that stupid circle…
     I don't know what it stands for. And if I don't know what it stands for,
     90% of the people out there is not going to understand what it stands
     for."*  The word is the fix, not a different glyph. Two words teach a
     newcomer the name AND the purpose, and the microphone stays where it
     belongs — in her ask bar and on Home's search bar, the Google shape. */
  /* ⚠️ 1 October 2026: "Zaryna", one word like the other four tabs — Al's
     yes, now that her mark says who she is (the speaking Z). "Ask Zaryna"
     is still the words on her own ask bar. */
  { key: 'zaryna',  label: 'Zaryna',  href: '?zaryna=1' },
  { key: 'saved',   label: 'Saved',   href: '?saved=1' },
  { key: 'profile', label: 'Profile', href: '?profile=1' },
];

/* ⚠️ TWO TABS CARRY AN EXTRA CLASS AND IT IS NOT DECORATION. `.zar` is what
   lifts her circle above the bar; `.prof` is what gives the avatar its two
   states. Without them both render as ordinary icons in the row. */
const EXTRA = { zaryna: ' zar', profile: ' prof' };

export function TabBar({ active }) {
  return (
    <nav className="ptabbar">
      {NAV.map((t) => {
        const Icon = ICONS[t.key];
        const on = t.key === active;
        const cls = 'ptab' + (EXTRA[t.key] || '') + (on ? ' active' : '');
        const inner = (
          <>
            <Icon />
            <span className="ptl">{t.label}</span>
          </>
        );
        return t.href
          ? <a key={t.key} className={cls} href={t.href}
              aria-current={on ? 'page' : undefined}>{inner}</a>
          : <span key={t.key} className={cls + ' notready'}
              aria-disabled="true">{inner}</span>;
      })}
    </nav>
  );
}

/* ⚠️ NUMBERED PAGES, NOT "LOAD MORE". Al asked for this on the View All
   pages, and Explore is the same list — somebody who opened a venue from page
   six must be able to come back to page six. A scroll-to-load list cannot do
   that, and looks fine right up until they try.

   ⚠️ IT LIVES HERE BECAUSE TWO SCREENS DRAW IT. It was written inside
   Explore.jsx when Explore was the only one; ViewAll needs the same pager and
   the five View All pages are that component. Copying it would be the fault
   behind /v1, the sixteen connector files and the two sign-in doors — and the
   copy that drifts is always the one nobody is looking at. */
/* ══════════════════════════════════════════════════════════════════════════
 * ⚠️⚠️ ONE SEARCH BAR, AND BOTH SCREENS DRAW THIS ONE — 24 SEPTEMBER 2026
 *
 * Al, 24 September 2026: *"you need to make the search/ask a question for
 * zaryna like you see on google. in fact the search bar on the home screen
 * should also be the same as the google search bar and mic."*
 *
 * ⚠️ THIS IS AL'S EXPLICIT CURRENT INSTRUCTION — PRECEDENCE #1, ABOVE THE
 * LOCKED SCREENS. `10_Home_LOCKED.html .search` puts an orange circular button
 * at the RIGHT and `08f_Zaryna.html .bar` has no magnifier at all. Both are
 * departed from here, on his say-so, and it is written down so nobody later
 * "restores the locked screen" and undoes it.
 *
 * THE SHAPE, and it is Google's: the magnifier at the LEFT, the words in the
 * middle, the microphone at the RIGHT.
 *
 * ⚠️ ONE COMPONENT AND ONE BLOCK OF CSS (`.ebar` in eleat.css), NEVER TWO.
 * Two copies of a card is how the heart went missing from five screens at
 * once and nobody saw it, because each screen looked plausible on its own.
 * A second bar would drift exactly the same way.
 *
 * ⚠️ AND HOME STILL DOES NOT GROW A SECOND SEARCH. There is one search in
 * Eleat and it is Explore's; Home hands the words over as `?explore=1&q=…`.
 * This component owns the SHAPE, never the destination — `onSubmit` is the
 * screen's, and the two screens send their words to different places.
 * ══════════════════════════════════════════════════════════════════════════ */

/** The microphone, drawn once. Rebuilt in `Zaryna.jsx` on 24 September after
 *  Al: *"it's not even a fucking microphone… it's so light, it's not even well
 *  seen."*  That markup moved here rather than being drawn a second time. */
export function MicIcon() {
  return (
    <svg viewBox="0 0 24 24" width="19" height="19" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round"
      strokeLinejoin="round" aria-hidden="true">
      <rect x="9" y="2" width="6" height="11" rx="3" />
      <path d="M5 10.5a7 7 0 0014 0" />
      <path d="M12 17.5V21" />
    </svg>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
 * ⚠️⚠️ THE LOCATION PIN LEFT THE SEARCH BAR — 24 SEPTEMBER 2026
 *
 * Al, looking at his own phone: *"the location, search bar and mic look too
 * cramped you can put location on the top bar and expand the search bar and
 * mic."*  On his screenshot the one bar held a magnifier, the pin, a truncated
 * placeholder and a microphone pushed off the end: three controls fighting for
 * one row on a phone.
 *
 * ⚠️ IT MOVED. IT DID NOT CHANGE. Same pin, same label, same `?location=1`,
 * same "Set location" when Eleat does not know where anybody is. Al, and RULE
 * ZERO-B: *"the location pin is a pin"* — never a place name, and "Central" is
 * sample content.
 *
 * ⚠️ AND IT IS DRAWN HERE, NOT IN `Home.jsx`, for the reason everything else
 * in this file is: the moment a second screen wants a pin, there must be one
 * pin to want. Home says WHERE it sits; this says what it is.
 *
 * ⚠️ THERE IS ROOM IN THE TOP BAR because the locked screen's own late cascade
 * switches the Profile icon off there —
 * `10_Home_LOCKED.html: .topbar .icbtn[title="Profile"]{display:none!important}`.
 * ══════════════════════════════════════════════════════════════════════════ */
export function LocationPin({ label, href }) {
  return (
    <button type="button" className="here"
      onClick={() => { go(href); }}
      aria-label="Change your location">
      <svg viewBox="0 0 24 24" width="13" height="13" fill="none"
        stroke="currentColor" strokeWidth="2" strokeLinejoin="round"
        aria-hidden="true">
        <path d="M12 21s-7-6.3-7-11a7 7 0 0114 0c0 4.7-7 11-7 11z" />
        <circle cx="12" cy="10" r="2.4" />
      </svg>
      <span className="herelabel">{label}</span>
    </button>
  );
}

/**
 * The one search bar.
 *
 * @param className  `hsearch` on Home, `zask` on Zaryna — the modifier that
 *                   decides where the bar SITS. Everything inside it is shared.
 * @param mic        `{ on, onTap, disabled, label }`, or null to draw none.
 * @param after      anything that belongs at the far right — Zaryna's `➤` and
 *                   her call button. Home has none.
 *
 * ⚠️ NO PIN. It used to sit between the magnifier and the words on Home, with
 * a hairline divider, and it made the row too cramped to hold the microphone.
 * It is `LocationPin` above, in Home's top bar. The bar is magnifier · words ·
 * microphone on BOTH screens now, which is also what made the two identical.
 */
export function SearchBar({
  className = '', value, onChange, onSubmit, placeholder,
  goLabel = 'Search', mic = null, after = null,
  inputDisabled = false, onFocus, onBlur, ownSend = false,
}) {
  /* ⚠️ TYPED → THE MICROPHONE BECOMES SEND (Al-Noor, 5 Nov 2026: "I typed and
     clicked the mic. Then ended up clicking on the magnifier."). ChatGPT's,
     WhatsApp's and Gemini's box: microphone while empty, send arrow once there
     are words. ➤ is Eleat's own send glyph (Zaryna's .zsend). A screen that
     already draws its own ➤ (Zaryna, `ownSend`) just hides the microphone. */
  const typed = !!String(value || '').trim() && !(mic && mic.on);
  return (
    <form className={'ebar ' + className}
      onSubmit={(e) => { e.preventDefault(); onSubmit(); }}>
      {/* ⚠️ A MARK, NOT A BUTTON — Al-Noor, 5 Nov 2026, with Google's own bar:
          "the confusion is the magnifier and mic." Google's left icon is its own
          logo, not tappable — so ours is Eleat's mark ("why not our Eleat
          logo… and write Search Eleat"); the keyboard's search key sends, and once words are typed
          the microphone's place becomes ➤ (below). */}
      <span className="ebar-go" aria-hidden="true"><img src="/eleat_mark.png" alt="" /></span>

      <input value={value} onChange={(e) => onChange(e.target.value)}
        onFocus={onFocus} onBlur={onBlur}
        placeholder={placeholder} aria-label={placeholder}
        disabled={inputDisabled} />

      {typed && !ownSend && (
        <button type="submit" className="zsend" title="Send" aria-label={goLabel}>➤</button>
      )}

      {mic && !typed && (
        <button type="button" className={mic.on ? 'zmic on' : 'zmic'}
          onClick={mic.onTap} disabled={!!mic.disabled}
          aria-label={mic.on ? 'Stop listening' : (mic.label || 'Speak')}
          aria-pressed={!!mic.on}>
          {/* while listening it is a STOP square (Al-Noor, 31 Oct 2026: people
              did not know to tap again to stop) */}
          {mic.on
            ? <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true"><rect x="6" y="6" width="12" height="12" rx="2.5" fill="currentColor" /></svg>
            : <MicIcon />}
        </button>
      )}

      {after}
    </form>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
 * ⚠️ THE MICROPHONE'S WIRING, ONCE — NOT ONCE PER SCREEN
 *
 * Two screens now carry a microphone, and both need the same three answers:
 * can this phone listen, has Super Admin turned it on, and is it listening
 * right now. Written twice they would drift; the second copy is always the
 * one nobody is looking at.
 *
 * ⚠️ WHAT HAPPENS WITH THE WORDS IS THE SCREEN'S, and the two differ: Home
 * runs a search, Zaryna asks a question. So the callbacks come in and nothing
 * about the destination lives here.
 *
 * ⚠️ AND IT IS OFF UNTIL THE SERVER SAYS OTHERWISE. `GET /zaryna/voice` is
 * requireAuth, so a signed-out Eleater sees no microphone at all — a wobble,
 * or no sign-in, turns voice OFF and never on.
 * ══════════════════════════════════════════════════════════════════════════ */
export function useSpeech({ onPartial, onDone, onQuiet, onError } = {}) {
  /* the switch already known this visit draws the microphone at once (voice.js) */
  const [flags, setFlags] = React.useState(() => knownVoiceFlags() || { mic: { enabled: false }, live: { enabled: false } });
  const [hearing, setHearing] = React.useState(false);
  /* 'opening' · 'listening' · 'writing' — what the screen tells them (MicStatus) */
  const [phase, setPhase] = React.useState(null);
  const [level, setLevel] = React.useState(0);
  const stop = React.useRef(null);
  /* ⚠️ THE CALLBACKS GO THROUGH A REF. `listen()` holds whichever functions it
     was given for the whole recognition; a screen that re-renders mid-sentence
     would otherwise have its words handed to a stale closure. */
  const cb = React.useRef({});
  cb.current = { onPartial, onDone, onQuiet, onError };

  React.useEffect(() => {
    /* ⚠️ NO SIGN-IN NEEDED TO SEE THE MICROPHONE — 1 October 2026. Visitors
       browse first (Al, 30 Sep), and Home's microphone is the phone's own
       speech feeding the ordinary search: it costs nothing and needs no
       account. This line returned early with no token, so no visitor ever
       saw it. GET /zaryna/voice is readable without one for the same reason;
       Zaryna's own screen still asks for a sign-in before she listens. */
    let alive = true;
    voiceFlags().then((f) => { if (alive && f) setFlags(f); });
    return () => { alive = false; if (stop.current) stop.current(); };
  }, []);

  /* Eleat's own speech-to-text when the Zaryna server has it (voice.js,
     listenOnEleat); the phone's recogniser only until then */
  const onEleat = !!(flags.transcribe && flags.transcribe.enabled) && recordAvailable();
  const allowed = !!(flags.mic && flags.mic.enabled) && (onEleat || micAvailable());

  function tap() {
    if (hearing) { if (stop.current) stop.current(); setHearing(onEleat); return; }
    /* the microphone is for Eleaters — a visitor gets the join sheet (22 Oct 2026) */
    if (needsAccount('talk', { ret: window.location.search || '?' })) return;
    setHearing(true); setPhase('opening'); setLevel(0);
    const end = () => { setHearing(false); setPhase(null); setLevel(0); };
    /* ⚠️ LIVE LISTENING FIRST (Al-Noor yes, 31 Oct 2026): their words appear in
       the box as they speak, and a finished sentence + a pause ends it —
       voice.js hearLive, which falls back to the recorded question itself */
    if (flags.hear_live && flags.hear_live.enabled && recordAvailable()) {
      stop.current = hearLive({
        lang: myLanguage(),
        onReady: () => setPhase('listening'),
        onLevel: (v) => setLevel(v),
        onPartial: (t) => cb.current.onPartial && cb.current.onPartial(t),
        onSending: () => { setPhase('writing'); setLevel(0); },
        onDone: (t) => { end(); if (cb.current.onPartial) cb.current.onPartial(t); if (cb.current.onDone) cb.current.onDone(t); },
        onQuiet: () => { end(); if (cb.current.onQuiet) cb.current.onQuiet(); },
        onError: (e) => { end(); if (cb.current.onError) cb.current.onError(e); },
      });
      return;
    }
    if (onEleat) {
      stop.current = listenOnEleat({
        lang: myLanguage(),
        onReady: () => setPhase('listening'),
        onLevel: (v) => setLevel(v),
        onSending: () => { setPhase('writing'); setLevel(0); },
        onDone: (t) => { end(); if (cb.current.onPartial) cb.current.onPartial(t); if (cb.current.onDone) cb.current.onDone(t); },
        onQuiet: () => { end(); if (cb.current.onQuiet) cb.current.onQuiet(); },
        onError: (e) => { end(); if (cb.current.onError) cb.current.onError(e); },
      });
      return;
    }
    setPhase('listening');
    stop.current = listen({
      /* ⚠️ THE LANGUAGE THEY CHOSE IN ELEAT, then the phone's — never a
         hard-coded 'en'. This read the phone alone, so somebody who set Eleat
         to 繁體中文 on an English handset dictated into an English recogniser
         with their own choice sitting unused one screen away. */
      lang: speechTag(myLanguage()),
      onPartial: (t) => cb.current.onPartial && cb.current.onPartial(t),
      onDone: (t) => { end(); if (cb.current.onDone) cb.current.onDone(t); },
      onQuiet: () => { end(); if (cb.current.onQuiet) cb.current.onQuiet(); },
      onError: (e) => { end(); if (cb.current.onError) cb.current.onError(e); },
    });
  }

  return { flags, allowed, hearing, tap, phase, level };
}

/* ══════════════════════════════════════════════════════════════════════════
 * ⚠️ WHAT THE MICROPHONE IS DOING, IN WORDS — Al-Noor, 11 Oct 2026: "when the
 * mic is pressed you don't know when to speak and how long to wait for the
 * answer." Under every bar that has a microphone (Home, Explore, Zaryna):
 *   opening   → "Getting the microphone ready…"
 *   listening → "Listening…" + a bar that moves with the voice, and
 *               "Stops when you pause, or tap the microphone"
 *   writing   → Zaryna's Z turning + "Thinking…" (Al-Noor, 30 Oct 2026: one
 *               "…ing" word per step, as Gemini, Claude and Google AI Mode do)
 * One component, so the three screens cannot say three different things.
 * ══════════════════════════════════════════════════════════════════════════ */
export function MicStatus({ speech }) {
  if (!speech || !speech.phase) return null;
  /* ⚠️ ONE CLEAN LINE — Al-Noor, 31 Oct 2026: "too tight and looks messy and
     cluttered" under Home's search bar (a level bar, two lines and a turning
     Z). Now: her face, gently pulsing, and one word — the shape of Claude's
     own "Generating…", which he pointed at. */
  if (speech.phase === 'writing') {
    return (
      <div className="micstatus" role="status">
        <ZarynaFace working /> <span>Thinking…</span>
      </div>
    );
  }
  return (
    <div className="micstatus on" role="status">
      <span className="miclevel" aria-hidden="true"><i style={{ transform: `scaleX(${Math.max(0.06, speech.level || 0)})` }} /></span>
      <span><b>Listening…</b> tap to stop</span>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
 * ⚠️⚠️ ZARYNA'S MARK — HER PHOTOGRAPH IS GONE, EVERYWHERE
 *
 * Al, 24 September 2026: *"then we dont need a zaryna profile pic and because
 * google and siri dont have one. so why should we."*
 *
 * ⚠️ HIS EXPLICIT CURRENT INSTRUCTION, PRECEDENCE #1, ABOVE THE LOCKED
 * SCREENS. `08f_Zaryna.html .zav` is `<img src="zaryna.jpg">` and
 * `10_Home_LOCKED.html` puts the same photograph in the tab bar. Both are
 * departed from here, deliberately. Her NAME stays; the face does not.
 *
 * ⚠️ NOT A FACE, NOT A STOCK IMAGE, AND NOT ONE NEW COLOUR — RULE ZERO-A.
 * A ring, two arcs and a core, in `var(--action)`, which is the palette's own
 * persimmon and the colour her tab has always been ringed in.
 *
 * ⚠️ AND IT PULSES WHILE SHE IS LISTENING, which is the one thing a
 * photograph could never do. `Zaryna.jsx` already has the `hearing` state; the
 * mark reads it. Motion is not compulsory — `prefers-reduced-motion` stops it,
 * for the same reason the splash does.
 * ══════════════════════════════════════════════════════════════════════════ */
/* ⚠️ HER WORDS ARE SHOWN AS PLAIN TEXT — Al-Noor, 8 Oct 2026: her answer
   showed **Hancham Korean Meathouse** with the asterisks. She is told to write
   plain text; anything that still slips through is cleaned here, once, for
   every screen that shows her answer. */
export function plainAnswer(text) {
  return String(text || '')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/__(.+?)__/g, '$1')
    .replace(/^\s*[*-]\s+/gm, '• ')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/\*\*/g, '');
}

/* ══════════════════════════════════════════════════════════════════════════
 * WHAT SHE IS DOING, IN THREE STEPS — Al-Noor, 10 Oct 2026: "it says what it's
 * searching for… the person sees that it's searching for what the question
 * was asked." Their own words first, then two steps, while her Z turns. Same
 * shimmer as before. Replaces the 6-second "Still looking" line. Written by
 * the app, so it costs nothing; translated by the dictionary ({} = their words).
 * ══════════════════════════════════════════════════════════════════════════ */
const STEP_MS = [0, 2500, 5000];
/* ⚠️ HER STEPS SAY "YOU", NOT "ME" — Al-Noor, 11 Oct 2026: "shouldn't it be
   searching Eleat for the best wings near you, not near me?" She is the one
   speaking, so their "me / my / I" becomes her "you / your". English only —
   any other language is shown as they wrote it. */
export function toYou(q) {
  const t = String(q || '').replace(/\s+/g, ' ').trim().replace(/[?？！!。.]+$/, '');
  if (!/^[\x00-\x7F]*$/.test(t)) return t;
  return t
    .replace(/\bI am\b/gi, 'you are').replace(/\bI'm\b/gi, "you're").replace(/\bI've\b/gi, "you've")
    .replace(/\bI'd\b/gi, "you'd").replace(/\bmyself\b/gi, 'yourself').replace(/\bmine\b/gi, 'yours')
    .replace(/\bmy\b/gi, 'your').replace(/\bme\b/gi, 'you').replace(/\bI\b/g, 'you');
}

/* ⚠️ WHAT SHE FOUND, BETWEEN THE SEARCH AND THE ANSWER — Al-Noor, 11 Oct 2026:
   "it's looking for the question, then it's found…" The server sends it
   (event 'found') once her shortlist is settled. */
export function foundLine(f) {
  if (!f) return null;
  const n = Number(f.n) || 0;
  if (f.none && f.what) return `Nothing near you serves ${f.what} — looking at what's close…`;
  /* whole sentences, so the translator finds each one (ui_strings.py) */
  if (f.specific && f.what && n) return n === 1 ? `Found ${n} place with ${f.what}…` : `Found ${n} places with ${f.what}…`;
  if (n) return n === 1 ? `Found ${n} place near you…` : `Found ${n} places near you…`;
  return 'Looking a little further…';
}

export function ZarynaSteps({ question, found }) {
  const [step, setStep] = React.useState(0);
  React.useEffect(() => {
    setStep(0);
    const ts = STEP_MS.slice(1).map((ms, i) => setTimeout(() => setStep(i + 1), ms));
    return () => ts.forEach(clearTimeout);
  }, [question]);
  /* once she has found them, that line, then "Picking…" a moment later */
  const [picked, setPicked] = React.useState(false);
  React.useEffect(() => {
    setPicked(false);
    if (!found) return undefined;
    const t = setTimeout(() => setPicked(true), 1800);
    return () => clearTimeout(t);
  }, [found]);
  const q = toYou(question);
  const short = q.length > 44 ? q.slice(0, 43).trim() + '…' : q;
  const words = found
    ? (picked ? 'Picking the best places for you…' : foundLine(found))
    : [
      short ? `Searching Eleat for “${short}”…` : 'Searching Eleat…',
      'Checking menus and offers nearby…',
      'Checking menus and offers nearby…',
    ][step];
  return <span className="zshim zsteps" key={words}>{words}</span>;
}

/* ══════════════════════════════════════════════════════════════════════════
 * ⚠️⚠️ HER FACE IS BACK — Al-Noor, 14 Oct 2026. Zaryna is named after his
 * mother and `zaryna.jpg` is a close likeness of her: a tribute, not a design
 * choice. "I don't think a character will do." Never remove it, never replace
 * it with a mark or a mascot. The locked screens' own photo
 * (`08f_Zaryna.html .zav`, `10_Home_LOCKED.html .pzwrap`). Her spinning Z
 * stays for the moments she is working (Al-Noor: "we still use the z
 * spinning"). `working` = a soft persimmon ring pulses around her face.
 * ══════════════════════════════════════════════════════════════════════════ */
export function ZarynaFace({ working = false }) {
  return (
    <span className={'zface' + (working ? ' working' : '')}>
      <img src="/zaryna_face.jpg" alt="Zaryna" />
    </span>
  );
}

export function ZarynaMark({ listening = false, speaking = false, thinking = false }) {
  /* ══════════════════════════════════════════════════════════════════════
     ⚠️⚠️ ZARYNA'S OWN MARK — THE SPEAKING Z IN ELEAT'S RING. Al, 1 October
     2026, choosing B from the sketches: a Z made of voice bars, inside the
     ring of Eleat's "e". His own idea — "turn it vertical… make it a Z and
     make it a speaking Z… that way it's animated and it's a Z."

     WHY NOT A SPARKLE: he rejected it as generic, and it is — it is
     Gemini's. Siri has its orb, Alexa its ring; this is the one only Eleat
     can have: her initial, her voice, inside the brand's own ring.

     ⚠️ STILL IN THE BOTTOM BAR; IT MOVES ONLY WHILE SHE LISTENS OR SPEAKS —
     the bars stretch like a voice. Never the only signal (the microphone
     lights at the same moment), and it holds still for anybody who has asked
     their phone to reduce motion. Persimmon (`currentColor` from `.zmark`,
     which is `var(--action)`); no new colour.
     ══════════════════════════════════════════════════════════════════════ */
  /* ⚠️ WHILE SHE WORKS OUT AN ANSWER THE Z TURNS — Al-Noor, 8 Oct 2026:
     "that Z that is for Zaryna… should be twirling instead of… finding or
     answering." It replaces the word "Looking…". */
  const live = listening || speaking;
  return (
    <span className={'zmark' + (live ? ' live' : '') + (thinking ? ' spin' : '')} aria-hidden="true">
      <svg viewBox="0 0 48 48" width="100%" height="100%">
        <circle cx="24" cy="24" r="21" fill="none" stroke="currentColor" strokeWidth="3.5" />
        <g fill="currentColor">
          <rect className="zb ze1" x="14" y="12" width="20" height="4.5" rx="2.25" />
          <rect className="zb zm1" x="24.5" y="18" width="9" height="4.5" rx="2.25" />
          <rect className="zb zm2" x="19.5" y="22.5" width="9" height="4.5" rx="2.25" />
          <rect className="zb zm3" x="14.5" y="27" width="9" height="4.5" rx="2.25" />
          <rect className="zb ze2" x="14" y="32.5" width="20" height="4.5" rx="2.25" />
        </g>
      </svg>
    </span>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
 * THE WAVEFORM — a spoken conversation, and it replaces a ROTARY TELEPHONE.
 *
 * Al, 27 September 2026: *"the telephone, what the fuck is the telephone and
 * whoever uses a fucking dial-up telephone as an icon… it doesn't go through a
 * telephone network, why the fuck are you trying to take it to a telephone
 * network, this is stupid… How does ChatGPT do it? How does Siri do it? They
 * don't have a fucking phone."*
 *
 * ⚠️ HE IS RIGHT ON BOTH COUNTS — THE GLYPH AND THE IDEA BEHIND IT. It was
 * `☏` (U+260F), a rotary handset, for a thing that is not a call, makes no
 * call, and has no number. A handset promises a telephone network.
 *
 * ⚠️ AND THE ANSWER IS NOT AN OPINION, IT IS THE CONVENTION BOTH MAJOR
 * ASSISTANTS SETTLED ON: a **microphone** turns speech into typed words, a
 * **waveform** opens a spoken conversation where the assistant talks back.
 * ChatGPT uses exactly that pair; Gemini Live uses a waveform too. So the two
 * controls in Eleat's ask bar now say what they each do, instead of being two
 * round outlines one of which is a telephone.
 * ═══════════════════════════════════════════════════════════════════════════ */
export function Waveform() {
  return (
    <svg viewBox="0 0 24 24" width="100%" height="100%" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round"
      aria-hidden="true">
      <path d="M3 12v0" /><path d="M7 8.5v7" /><path d="M11 5v14" />
      <path d="M15 8.5v7" /><path d="M19 10.5v3" />
    </svg>
  );
}


export function Pager({ page, pages, onGo }) {
  const near = [];
  for (let i = 0; i < pages; i++) {
    if (i === 0 || i === pages - 1 || Math.abs(i - page) <= 1) near.push(i);
  }
  const out = [];
  let last = -1;
  for (const i of near) {
    if (last >= 0 && i - last > 1) out.push('gap' + i);
    out.push(i); last = i;
  }
  return (
    <nav className="pager" aria-label="Pages">
      <button type="button" className="pbtn" disabled={page === 0}
        onClick={() => onGo(page - 1)} aria-label="Previous page">‹</button>
      {out.map((i) => (typeof i === 'string'
        ? <span key={i} className="pgap">…</span>
        : <button key={i} type="button"
            className={i === page ? 'pbtn on' : 'pbtn'}
            aria-current={i === page ? 'page' : undefined}
            onClick={() => onGo(i)}>{i + 1}</button>))}
      <button type="button" className="pbtn" disabled={page >= pages - 1}
        onClick={() => onGo(page + 1)} aria-label="Next page">›</button>
    </nav>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
 * ⚠️⚠️⚠️ STARS — ONE OWNER, BECAUSE THERE WERE THREE AND TWO WERE WRONG.
 *
 * Al, 29 August 2026, quoted in the locked `star-rating` script:
 * *"if a review says 4.5 out of 5 then why give it a 5 star. it should be 4.5
 * stars. NOT 5."*  and *"all restaurants should have the numerical number and
 * the amount of stars that the number correlates to."*
 *
 * ⚠️ WHAT WAS ACTUALLY SHIPPING — 27 September 2026, three implementations:
 *   · `Venue.jsx` header — `'★'.repeat(Math.round(rating))`, so **4.5 drew
 *     FIVE stars**, the exact thing he named;
 *   · `Reviews.jsx` — a string sliced by a fraction, which JavaScript
 *     truncates, so **4.5 drew FOUR**;
 *   · `Venue.jsx`'s own `Stars` — the correct partial fill, used by the review
 *     list and the score card and nowhere else.
 * **Two of the three disagreed with him and with each other, and all three
 * looked plausible on their own screen.** That is the drift this repository
 * pays for over and over: the copy nobody is looking at is the stale one.
 *
 * ⚠️ AND A PARTIAL FILL, NEVER A HALF-STAR CHARACTER — the locked script's own
 * reason: *"The ½-star glyph is missing from several fonts, and a missing glyph
 * is worse than no star."*  Five stars laid over five stars, the top one
 * clipped to the exact percentage, so 4.3 looks like 4.3.
 *
 * ⚠️ THE NUMBER AND THE PICTURE MUST AGREE OR NEITHER IS TRUSTED. Also the
 * locked script's: *"Five full stars beside 4.5 tells an Eleater one of the two
 * is decoration — and once they think that, they stop believing both."*
 * ══════════════════════════════════════════════════════════════════════════ */
/* PRICE — "$$ · Moderate". Al-Noor, 2 Nov 2026: the venue page and cards
 * must say how expensive a place is in dollar signs. The signs are the admin's
 * own price picker ("$ · Budget" … "$$$$ · Premium", discovery.py, 9 Oct); the
 * word is the taxonomy's name, never the key. One function for every card and
 * the venue page, so two screens cannot show two prices. */
export const PRICE_SIGNS = { budget: '$', moderate: '$$', upscale: '$$$', premium: '$$$$' };
export function priceBand(v) {
  const word = zh(v && v.price_band_en, v && v.price_band_zh) || null;
  const signs = v && PRICE_SIGNS[v.price_band];
  if (signs && word) return `${signs} \u00b7 ${word}`;
  return signs || word;
}

export function Stars({ value }) {
  const pct = Math.max(0, Math.min(100, (Number(value) / 5) * 100));
  return (
    <span className="estars" aria-label={`${Number(value).toFixed(1)} out of 5`}>
      <span className="eback">★★★★★</span>
      <span className="efill" style={{ width: `${pct}%` }} aria-hidden="true">★★★★★</span>
    </span>
  );
}
